import { useRef } from "react";
import { Camera, Trash, ImageSquare, FolderOpen } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Multi-photo uploader with base64 encoding + thumbnail preview.
 *
 * Ofrece dos entradas separadas — Galería (sin `capture`) y Cámara
 * (`capture="environment"`) — para no forzar la cámara en móviles.
 *
 * Props:
 *  - value: List<string>  base64 dataURLs (or empty array)
 *  - onChange: (next: List<string>) => void
 *  - max?: number (default 6)
 *  - maxDim?: number (px) (default 1400) — long side downscale target
 *  - testidPrefix?: string
 */
export default function PhotoUploader({
  value = [],
  onChange,
  max = 6,
  maxDim = 1400,
  testidPrefix = "photo",
}) {
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const resize = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
          const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.82));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const remaining = max - value.length;
    if (remaining <= 0) {
      toast.error(`Máximo ${max} fotos por registro`);
      e.target.value = "";
      return;
    }
    const batch = files.slice(0, remaining);
    try {
      const encoded = await Promise.all(batch.map(resize));
      onChange([...value, ...encoded]);
      if (files.length > remaining) {
        toast.warning(`Solo se cargaron ${remaining} de ${files.length} — máx ${max}`);
      }
    } catch (err) {
      toast.error("No se pudo procesar la imagen");
    } finally {
      e.target.value = "";
    }
  };

  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));
  const canAdd = value.length < max;

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-uploader`}>
      <div className="flex flex-wrap gap-2">
        {value.map((src, i) => (
          <div
            key={i}
            className="relative w-20 h-20 rounded overflow-hidden border border-slate-200 group"
            data-testid={`${testidPrefix}-thumb-${i}`}
          >
            <img src={src} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white"
              data-testid={`${testidPrefix}-remove-${i}`}
              title="Eliminar"
            >
              <Trash size={16} weight="bold" />
            </button>
          </div>
        ))}
        {canAdd && (
          <>
            <button
              type="button"
              onClick={() => galleryRef.current?.click()}
              data-testid={`${testidPrefix}-add-gallery`}
              className="w-20 h-20 rounded border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50 flex flex-col items-center justify-center text-slate-500 hover:text-blue-700 transition"
              title="Elegir de la galería del dispositivo"
            >
              <FolderOpen size={18} weight="duotone" />
              <span className="text-[10px] font-semibold mt-0.5">Galería</span>
            </button>
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              data-testid={`${testidPrefix}-add-camera`}
              className="w-20 h-20 rounded border-2 border-dashed border-slate-300 hover:border-emerald-500 hover:bg-emerald-50 flex flex-col items-center justify-center text-slate-500 hover:text-emerald-700 transition"
              title="Tomar foto ahora con la cámara"
            >
              <Camera size={18} weight="duotone" />
              <span className="text-[10px] font-semibold mt-0.5">Cámara</span>
            </button>
          </>
        )}
      </div>
      {/* Galería: sin capture → abre selector normal del sistema */}
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onPick}
        data-testid={`${testidPrefix}-input-gallery`}
      />
      {/* Cámara: capture="environment" → abre cámara trasera en móvil */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPick}
        data-testid={`${testidPrefix}-input-camera`}
      />
      {/* Alias legacy — algunos tests apuntan a `-input` */}
      <input
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onPick}
        data-testid={`${testidPrefix}-input`}
      />
      <p className="text-[10px] text-slate-500 flex items-center gap-1">
        <ImageSquare size={11} /> {value.length} / {max} fotos · elige &quot;Galería&quot; para subir o &quot;Cámara&quot; para tomar foto
      </p>
    </div>
  );
}
